export type EvidenceStance =
  "support" | "disprove" | "inconclusive" | "partially_support";

export interface ClaimVerification {
  claim: string;
  questions: string[];
  evidence: string[];
  answers: string[];
  assessment: EvidenceStance;
}

export interface SentenceRefinement {
  original_sentence: string;
  claim_verifications: ClaimVerification[];
  refined_sentence: string;
  refinement_reasoning: string;
  was_modified: boolean;
}

export interface ChunkRefinement {
  original_chunk: string;
  source_text: string;
  sentence_refinements: SentenceRefinement[];
  line_break_indices: number[];
  had_problematic_claims: boolean;
}

export interface SummaryRefinement {
  chunk_refinements: ChunkRefinement[];
  refinement_model: string;
  evaluation_model: string;
  refinement_timestamp: string;
}

export interface ManuscriptMetadata {
  title: string;
  author: string;
  language: string;
  genres: string[];
}

export interface Manuscript {
  chunks: string[];
  metadata: ManuscriptMetadata;
}

export interface Character {
  name: string;
  age: string;
  pronouns: string;
  role: string;
  description: string;
  aliases: string[];
}

export interface Setting {
  setting_type: "primary" | "major" | "minor";
  location: string;
  time_period: string;
  location_label: string;
  time_period_label: string;
  local_social_structure: string;
}

export interface WorldSetting {
  settings: Setting[];
  selected_setting_indices?: number[] | null;
  social_structure: string;
  metaphysics: string;
  social_structure_label: string;
  metaphysics_label: string;
}

export interface Trope {
  name: string;
  description: string;
  reason: string;
}

export interface Summary {
  summary_id: string;
  paragraph_history: string[][][];
  manuscript: Manuscript;
  summary_uri: string | null;
  refinement: SummaryRefinement | null;
}

export interface ManuscriptJson {
  paragraphs: string[];
  metadata: unknown;
  word_count: number;
}

const AVERAGE_WORDS_PER_MINUTE = 250;

export interface ISummaryData {
  title: string;
  author: string;
  summary_paragraphs: string[];
  synopsis: string;
  manuscript_word_count?: number;
  get reading_time_minutes(): number | undefined;
  genres?: string[];
  world_setting?: WorldSetting;
  characters?: Character[];
  tropes?: Trope[];
}

export class SummaryData implements ISummaryData {
  title: string;
  author: string;
  summary_paragraphs: string[];
  synopsis: string;
  manuscript_word_count?: number;
  genres?: string[];
  world_setting?: WorldSetting;
  characters?: Character[];
  tropes?: Trope[];

  constructor(params: Omit<ISummaryData, "reading_time_minutes">) {
    this.title = params.title;
    this.author = params.author;
    this.summary_paragraphs = params.summary_paragraphs;
    this.synopsis = params.synopsis;
    this.manuscript_word_count = params.manuscript_word_count;
    this.genres = params.genres;
    this.world_setting = params.world_setting;
    this.characters = params.characters;
    this.tropes = params.tropes;
  }

  get reading_time_minutes(): number | undefined {
    return this.manuscript_word_count != null
      ? Math.round(this.manuscript_word_count / AVERAGE_WORDS_PER_MINUTE)
      : undefined;
  }
}
